import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Compass, TrendingUp, Sparkles, ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1542338332-76971ae8c292?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxtYXR1cmUlMjBjb3VwbGUlMjBzbWlsaW5nJTIwY29mZmVlfGVufDB8fHx8MTc4NTQ0OTE1N3ww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t } = useLang();
  const { user } = useAuth();

  return (
    <div data-testid="landing-page">
      <section className="mx-auto max-w-6xl px-6 pt-12 pb-20 md:pt-20 md:pb-28">
        <div className="grid gap-14 md:grid-cols-2 md:items-center">
          <div className="max-w-xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-[#101A42]/10 px-4 py-2 text-sm font-semibold text-[#101A42]">
              <Sparkles className="h-4 w-4" />
              {t("app_tagline")}
            </div>
            <h1
              data-testid="hero-title"
              className="font-serif text-4xl font-semibold leading-tight text-[#101A42] sm:text-5xl lg:text-6xl"
            >
              {t("hero_title")}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-[#333333] sm:text-xl">
              {t("hero_subtitle")}
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Button
                asChild
                data-testid="cta-start-btn"
                className="min-h-[64px] rounded-full bg-[#A762B1] px-8 text-xl font-semibold text-white hover:bg-[#B775C0]"
              >
                <Link to={user ? "/onboarding" : "/register"}>
                  {t("start_now")}
                  <ArrowRight className="ml-2 h-6 w-6" />
                </Link>
              </Button>
              {!user && (
                <Button
                  asChild
                  variant="outline"
                  data-testid="cta-login-btn"
                  className="min-h-[64px] rounded-full border-2 border-[#101A42] px-8 text-xl font-semibold text-[#101A42] hover:bg-[#101A42] hover:text-white"
                >
                  <Link to="/login">{t("already_account")}</Link>
                </Button>
              )}
            </div>
            {!user && (
              <div className="mt-5 flex flex-wrap items-center gap-6">
                <Link
                  to="/decouvrir"
                  data-testid="cta-guest-link"
                  className="inline-flex items-center gap-2 text-lg font-semibold text-[#101A42] underline underline-offset-4 hover:text-[#A762B1]"
                >
                  {t("try_guest")}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/simulateur"
                  data-testid="cta-simulator-link"
                  className="inline-flex items-center gap-2 text-lg font-semibold text-[#A762B1] underline underline-offset-4 hover:text-[#101A42]"
                >
                  {t("try_simulator")}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </div>

          <div className="relative">
            <div className="absolute -inset-6 rounded-[3rem] bg-[#101A42]/10 rotate-3" />
            <img
              src={HERO_IMG}
              alt=""
              className="relative rounded-[2.5rem] shadow-[0_20px_60px_rgba(30,63,51,0.18)] object-cover w-full aspect-[4/5]"
            />
          </div>
        </div>
      </section>

      <section className="border-y border-[#E8DFC7] bg-white/60 py-16">
        <div className="mx-auto grid max-w-6xl gap-10 px-6 md:grid-cols-3">
          {[
            { icon: Compass, tKey: "feature_1", testid: "feature-1" },
            { icon: TrendingUp, tKey: "feature_2", testid: "feature-2" },
            { icon: Sparkles, tKey: "feature_3", testid: "feature-3" },
          ].map(({ icon: Icon, tKey, testid }) => (
            <div key={tKey} data-testid={testid} className="flex gap-5">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#A762B1]/15 text-[#A762B1]">
                <Icon className="h-7 w-7" strokeWidth={2} />
              </div>
              <div>
                <h3 className="font-serif text-2xl font-semibold text-[#101A42]">
                  {t(`${tKey}_title`)}
                </h3>
                <p className="mt-2 text-lg leading-relaxed text-[#4A4A4A]">
                  {t(`${tKey}_desc`)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
